import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { FichaResponse, ItemResponse, LinhaSimuladaResponse, RendimentoGrupo } from '@triade/contracts';
import { useAnaliticos } from '@/api/centros-custo';
import { useFicha, useFichasDoItem, useMarcarFichaEscolhida, useNovaVersaoFicha, useSalvarComponentes, useSimulacaoFicha } from '@/api/fichas';
import { useFornecedores } from '@/api/fornecedores';
import { usePreviaImpactoEscolhida } from '@/api/impacto-cascata';
import { useItem, useItens } from '@/api/itens';
import { usePode } from '@/api/me';
import { avisarDesfazer, HistoricoRegistro } from '@/components/desfazer';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { useConfirmarImpacto } from '@/components/impacto-cascata/use-confirmar-impacto';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { SeloProtheus } from '@/components/integracoes-erp/selo-protheus';
import { BotaoRelatorios } from '@/relatorios';
import { ROTULO_TIPO } from '@/components/item-cadastro/tipos';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { formatarCusto } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useSessao } from '@/lib/session';
import { ListaAtencao, itensDeAtencao } from './atencao-linhas';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { GradeMateriais } from './grade-materiais';
import { colunasComErro, errosDaResposta, errosDaSimulacao, listaDeErros, type ErroLinhaFicha } from './erros-salvar';
import { GradeProcesso } from './grade-processo';
import { HistoricoEscolhas } from './historico-escolhas';
import { PainelCustoFicha } from './painel-custo-ficha';
import { CascataFicha } from './cascata-ficha';
import { UsadaEm } from './usada-em';
import { motivoSaidasNaoEditavel, SaidasFicha } from './saidas-ficha';
import { RateioFicha } from './rateio-ficha';
import {
  assinatura,
  deFicha,
  GRUPOS_RENDIMENTO,
  novaEtapa,
  novaLinha,
  paraLote,
  paraSimular,
  perdaErro,
  rendimentoDe,
  rendimentoParaApi,
  ROTULO_GRUPO_RENDIMENTO,
  TIPO_DO_ITEM,
  tiposDeMaterial,
  type CentroOpcao,
  type LinhaFicha,
  type RendimentoTexto,
  type TipoComponente,
} from './tipos';
import { VersoesFicha } from './versoes-ficha';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/** Espera entre a ultima edicao e o pedido do custo a API (previa sem gravar); a requisicao anterior em voo e cancelada. */
export const ATRASO_SIMULACAO_MS = 400;

const ROTULO_CAMPO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

/** Tela "Ficha técnica" (/custos/fichas-tecnicas/$id): cabeçalho do item, processo, materiais, versões. Largura total. */
export function FichaPage({ id }: { id: string }) {
  const { empresaId } = useSessao();
  const ficha = useFicha(id);
  const item = useItem(ficha.data?.itemId);
  // Sessao 49 (C): custo de hoje por versao so para quem ve custo (sem custo.ver a API nem calcula).
  const { pode } = usePode();
  const versoes = useFichasDoItem(ficha.data?.itemId, { comCustoHoje: pode('custo.ver') });

  let corpo;
  if (empresaId === null) corpo = <Vazio>Escolha a empresa no menu lateral para operar.</Vazio>;
  else if (ficha.isError) corpo = <ErroFormacao titulo="Não foi possível carregar a ficha" erro={ficha.error} onTentar={() => void ficha.refetch()} />;
  else if (item.isError) corpo = <ErroFormacao titulo="Não foi possível carregar o item" erro={item.error} onTentar={() => void item.refetch()} />;
  else if (versoes.isError) corpo = <ErroFormacao titulo="Não foi possível carregar as versões" erro={versoes.error} onTentar={() => void versoes.refetch()} />;
  else if (!ficha.data || !item.data || !versoes.data) corpo = <Vazio papel="status">Carregando ficha técnica…</Vazio>;
  else corpo = <Editor key={id} ficha={ficha.data} item={item.data} versoes={versoes.data} />;

  return <div className="flex w-full min-w-0 flex-col gap-5 pb-4">{corpo}</div>;
}

function Editor({ ficha, item, versoes }: { ficha: FichaResponse; item: ItemResponse; versoes: FichaResponse[] }) {
  const navigate = useNavigate();
  // PCP edita a estrutura sem R$; sem ficha.editar a tela e so consulta. A API e quem nega, aqui so se esconde.
  const { pode } = usePode();
  const comCusto = pode('custo.ver');
  // Ficha vinda do Protheus (estrutura em IMPORTAR): tudo somente leitura. Sem edicao, nao ha salvar, nova versao nem escolha.
  const doProtheus = ficha.doProtheus === true;
  const podeEditar = pode('ficha.editar') && !doProtheus;
  // Centros ANALITICOS (combobox da etapa e do centro de consumo): so com centro.ver.
  const analiticos = useAnaliticos(false, pode('centro.ver'));
  // Fornecedores (custo.ver) para a coluna Fornecedor de material comprado; sem a permissao a API responde 403: nao consulta.
  const fornecedores = useFornecedores({}, { ativo: comCusto });
  const centros = useMemo<CentroOpcao[]>(
    () =>
      (analiticos.data ?? []).map((c) => ({
        id: c.id,
        codigo: c.codigo,
        nome: c.nome,
        caminho: c.caminho,
        natureza: c.natureza,
        classificacao: c.classificacao,
      })),
    [analiticos.data],
  );
  const inicial = useMemo(() => deFicha(ficha), [ficha]);
  const [linhas, setLinhas] = useState<LinhaFicha[]>(inicial);
  const [descricao, setDescricao] = useState(ficha.descricao ?? '');
  // Validacao ao tentar salvar (Sessao 37): linhas e campos marcados + a lista "Corrija: ...". Some assim que o usuario edita.
  const [errosLinhas, setErrosLinhas] = useState<ErroLinhaFicha[]>([]);
  useEffect(() => {
    setErrosLinhas((atual) => (atual.length > 0 ? [] : atual));
  }, [linhas]);
  const marcasDeErro = useMemo(() => colunasComErro(errosLinhas), [errosLinhas]);
  // Rendimento do processo (percentual digitado + grupo). Ficha em uso: so leitura (vira nova versao ao salvar).
  const [rendimento, setRendimento] = useState<RendimentoTexto>(() => rendimentoDe(ficha));
  const [base, setBase] = useState(() => assinatura(inicial, ficha.descricao ?? '', rendimentoDe(ficha)));
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [irPara, setIrPara] = useState<string | null>(null);
  // Sessao 37: ao salvar uma ficha que nao e a ativa do produto, pergunta se quer marca-la (a navegacao para a versao nova espera a resposta).
  const [perguntaAtiva, setPerguntaAtiva] = useState<{ fichaId: string; versao: number | null; depois: string | null } | null>(null);
  const [marcandoAtiva, setMarcandoAtiva] = useState(false);
  const [focarMat, setFocarMat] = useState<{ uid: string; n: number } | null>(null);
  const [focarEtapa, setFocarEtapa] = useState<{ uid: string; n: number } | null>(null);
  const [termo, setTermo] = useState('');
  const [perdaManual, setPerdaManual] = useState(false);
  const [trocaFeita, setTrocaFeita] = useState(false);
  const [guia, setGuia] = useState<'ficha' | 'cascata' | 'usada'>('ficha');
  const [atencaoAberta, setAtencaoAberta] = useState(false);

  const salvarComp = useSalvarComponentes();
  const qc = useQueryClient();
  const novaVersao = useNovaVersaoFicha();
  const escolher = useMarcarFichaEscolhida();
  // Troca da escolhida: a previa de impacto (sem gravar) vem antes do dialogo de confirmacao.
  const previaEscolha = usePreviaImpactoEscolhida();
  const { confirmar, dialogo } = useConfirmarImpacto();
  const salvando = salvarComp.isPending || novaVersao.isPending;

  const dirty = assinatura(linhas, descricao, rendimento) !== base;
  const rend = rendimentoParaApi(rendimento);
  // Ficha de coproduto (linha unica criada pelo sistema): o custo vem do rateio do principal. Nao se edita nem se grava.
  const ehCoproduto = (ficha.componentes ?? []).some((c) => c.tipo === 'COPRODUTO');
  const fichaPrincipal = ficha.materiais?.itens.find((m) => m.tipo === 'COPRODUTO')?.fichaId ?? null;
  const podeEditarComp = podeEditar && !ehCoproduto;
  const rendimentoEditavel = podeEditarComp && !ficha.emUso;

  // O servidor mudou (salvou, escolheu, recarregou): a grade volta a refletir o gravado, se nao houver edicao em curso.
  useEffect(() => {
    if (dirty) return;
    const l = deFicha(ficha);
    const r = rendimentoDe(ficha);
    setLinhas(l);
    setDescricao(ficha.descricao ?? '');
    setRendimento(r);
    setBase(assinatura(l, ficha.descricao ?? '', r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ficha]);

  const bloqueio = useBlockerDirty(dirty && irPara === null);
  useEffect(() => {
    if (irPara) void navigate({ to: '/custos/fichas-tecnicas/$id', params: { id: irPara } });
  }, [irPara, navigate]);

  // As duas secoes sao UMA lista de linhas (processo antes, materiais depois): cada grade devolve so a sua parte.
  const processo = useMemo(() => linhas.filter((l) => l.secao === 'processo'), [linhas]);
  const materiais = useMemo(() => linhas.filter((l) => l.secao === 'materiais'), [linhas]);
  // Centro de consumo do material: so os centros que ja tem etapa neste processo (a API recusa outro).
  const centrosDeConsumo = useMemo(() => {
    const ids = new Set(processo.flatMap((l) => (l.centroCustoId ? [l.centroCustoId] : [])));
    return centros.filter((c) => ids.has(c.id));
  }, [processo, centros]);
  const setProcesso = (nova: LinhaFicha[]) => setLinhas((atual) => [...nova, ...atual.filter((l) => l.secao !== 'processo')]);
  const setMateriais = (nova: LinhaFicha[]) => setLinhas((atual) => [...atual.filter((l) => l.secao === 'processo'), ...nova]);

  // Custo de linha, resolvido e total: sempre da API (POST /fichas-tecnicas/simular), com debounce.
  // Rendimento invalido nao vai para a simulacao (vira nulo): o aviso do campo diz o que corrigir.
  const { rendimentoProcesso, rendimentoGrupo } = rend;
  // Ficha de coproduto nao simula (a API nao recebe o tipo COPRODUTO): o custo e o gravado da ficha.
  const entrada = useMemo(
    () => (ehCoproduto ? null : { uids: linhas.map((l) => l.uid), componentes: paraSimular(linhas), rendimentoProcesso, rendimentoGrupo }),
    [ehCoproduto, linhas, rendimentoProcesso, rendimentoGrupo],
  );
  const entradaAtrasada = useDebounced(entrada, ATRASO_SIMULACAO_MS);
  const sim = useSimulacaoFicha(entradaAtrasada, item.id);
  const calculo = useMemo(() => {
    const m = new Map<string, LinhaSimuladaResponse>();
    sim.data?.uids.forEach((uid, i) => {
      const l = sim.data?.resposta.linhas[i];
      if (l) m.set(uid, l);
    });
    return m;
  }, [sim.data]);
  const calculando = entradaAtrasada !== entrada || sim.isFetching;
  const totalApi = sim.data?.resposta.custoUnitario ?? ficha.custoNaData?.total ?? ficha.custoUnitario ?? null;
  const comAlerta = [...calculo.values()].filter((l) => l.alertas.length > 0 || (l.avisos?.length ?? 0) > 0).length;
  const itensAtencao = useMemo(() => itensDeAtencao(linhas, calculo), [linhas, calculo]);
  // Composicao de horas (SERVICO): no lugar de Processo produtivo e Materiais. As linhas seguem na mesma lista (salvar as inclui).
  const servico = item.tipo === 'SERVICO';
  const perdaInvalida = linhas.map((l) => perdaErro(l)).find((e) => e !== null) ?? null;
  const perdaInvalidaApi = [...calculo.values()].some((l) => l.alertas.includes('PERDA_INVALIDA'));

  // Totais das secoes: gravados da API. Com alteracao nao salva o gravado nao vale para a secao: "…" ate salvar.
  // (O total da ficha, acima, e o que a API simulou para as linhas da tela.)
  const processoTotal = dirty ? null : (ficha.processo?.total ?? null);
  const materiaisTotal = dirty ? null : (ficha.materiais?.total ?? null);
  const materiaisSemi = dirty ? null : (ficha.materiais?.totalSemiacabados ?? null);

  // Avisos que a API manda para migracao: mao de obra solta num produto (o custo gravado vale, mas a linha deve virar etapa).
  const pendentes = ficha.alertasCusto?.filter((a) => a.code === 'MAO_DE_OBRA_FORA_DO_PROCESSO') ?? [];
  // Alertas da secao Saidas (subproduto ou residuo que deixa o custo invalido): a secao fica em vermelho com a mensagem.
  const errosSaidas = ficha.alertasCusto?.filter((a) => a.secao === 'SAIDAS') ?? [];

  const busca = useDebounced(termo.trim(), 250);
  const itens = useItens({ q: busca || undefined, ativo: true, limit: 20 });
  const candidatos = useMemo<ItemResponse[]>(() => (itens.data ?? []).filter((i) => i.id !== item.id), [itens.data, item.id]);

  const posicao = versoes.findIndex((v) => v.id === ficha.id);
  const maisAntiga = posicao >= 0 ? versoes[posicao + 1] : undefined;
  const maisNova = posicao > 0 ? versoes[posicao - 1] : undefined;

  function adicionarMaterial(parcial: Parameters<typeof novaLinha>[0] = {}) {
    const nova = novaLinha({ secao: 'materiais', ...parcial });
    setLinhas((atual) => [...atual, nova]);
    setFocarMat((f) => ({ uid: nova.uid, n: (f?.n ?? 0) + 1 }));
  }

  function adicionarEtapa() {
    const nova = novaEtapa();
    setLinhas((atual) => [...atual.filter((l) => l.secao === 'processo'), nova, ...atual.filter((l) => l.secao === 'materiais')]);
    setFocarEtapa((f) => ({ uid: nova.uid, n: (f?.n ?? 0) + 1 }));
  }

  function adicionarHoras() {
    adicionarMaterial({ tipo: 'MAO_DE_OBRA', unidade: 'h' });
  }

  function adicionarItem(escolhido: ItemResumo) {
    const completo = candidatos.find((i) => i.id === escolhido.id);
    adicionarMaterial({
      tipo: TIPO_DO_ITEM[completo?.tipo ?? escolhido.tipo ?? 'PRODUTO'],
      descricao: escolhido.descricao,
      componenteItemId: escolhido.id,
      itemCodigo: escolhido.codigo,
      itemTipo: completo?.tipo ?? escolhido.tipo ?? null,
      unidade: completo?.unidade ?? '',
    });
  }

  async function salvar() {
    setErro(null);
    if (ehCoproduto) return;
    // Validacoes locais de formato (perda vazia ou fora do formato, rendimento fora do contrato) e o que a API ja sinalizou.
    if (perdaInvalida) return setErro(perdaInvalida);
    if (rend.erro) return setErro(rend.erro);
    if (perdaInvalidaApi) return setErro('Há linha com perda inválida (a API sinaliza na linha). Corrija antes de salvar.');
    // O que a simulacao ja sabe que a API recusaria: marca a linha e o campo sem gastar uma chamada.
    const locais = errosDaSimulacao(linhas, calculo);
    if (locais.length > 0) {
      setErrosLinhas(locais);
      return setErro(`Corrija ${locais.length} ${locais.length === 1 ? 'linha destacada' : 'linhas destacadas'} antes de salvar.`);
    }
    try {
      const texto = descricao.trim() || null;
      if (ficha.emUso) {
        // Versao ja usada em formacao/orcamento e imutavel: a edicao vira a proxima versao.
        const nova = await novaVersao.mutateAsync({ fichaId: ficha.id, descricao: texto, componentes: paraLote(linhas) });
        setBase(assinatura(linhas, descricao, rendimento));
        setSalvoEm(new Date());
        // A versao nova nunca nasce como a ativa: pergunta antes de abrir (se pode escolher), senao so abre.
        if (podeEditar) setPerguntaAtiva({ fichaId: nova.id, versao: nova.versao ?? null, depois: nova.id });
        else setIrPara(nova.id);
      } else {
        const gravada = await salvarComp.mutateAsync({
          fichaId: ficha.id,
          descricao: texto,
          componentes: paraLote(linhas),
          rendimentoProcesso: rend.rendimentoProcesso,
          rendimentoGrupo: rend.rendimentoGrupo,
        });
        void avisarDesfazer({ recurso: 'fichas_tecnicas', chave: ficha.id, qc });
        const l = deFicha(gravada);
        const r = rendimentoDe(gravada);
        setLinhas(l);
        setDescricao(gravada.descricao ?? '');
        setRendimento(r);
        setBase(assinatura(l, gravada.descricao ?? '', r));
        setSalvoEm(new Date());
        if (!ficha.escolhida && podeEditar) setPerguntaAtiva({ fichaId: ficha.id, versao: ficha.versao, depois: null });
      }
    } catch (e) {
      // 400 (Zod, com o caminho do campo) e 422 (com a linha em `details`): marca a linha e o campo e lista o que corrigir.
      const marcados = errosDaResposta(linhas, e);
      if (marcados.length > 0) {
        setErrosLinhas(marcados);
        setErro(`Corrija ${marcados.length} ${marcados.length === 1 ? 'linha destacada' : 'linhas destacadas'} antes de salvar.`);
      } else setErro(mensagemErro(e));
    }
  }

  async function criarNovaVersao() {
    setErroAcao(null);
    try {
      const nova = await novaVersao.mutateAsync({ fichaId: ficha.id });
      setIrPara(nova.id);
    } catch (e) {
      setErroAcao(mensagemErro(e));
    }
  }

  async function marcarEscolhida(id: string) {
    setErroAcao(null);
    try {
      const confirmado = await confirmar(() => previaEscolha.mutateAsync({ fichaId: id, escolhida: true }));
      if (!confirmado) return;
      await escolher.mutateAsync({ fichaId: id, escolhida: true });
      setTrocaFeita(true);
    } catch (e) {
      setErroAcao(mensagemErro(e));
    }
  }

  /** Resposta da pergunta "Marcar como a ficha ativa de <produto>?": sim troca a escolhida (as regras de kit e de permissao sao da API); nao so segue. */
  async function responderAtiva(marcar: boolean) {
    const p = perguntaAtiva;
    if (!p) return;
    setMarcandoAtiva(true);
    try {
      if (marcar) {
        // Cancelar no impacto nao troca nada: a pergunta continua aberta (pode responder "Não").
        const confirmado = await confirmar(() => previaEscolha.mutateAsync({ fichaId: p.fichaId, escolhida: true }));
        if (!confirmado) return;
        await escolher.mutateAsync({ fichaId: p.fichaId, escolhida: true });
      }
      setPerguntaAtiva(null);
      if (marcar) setTrocaFeita(true);
      if (p.depois) setIrPara(p.depois);
    } catch (e) {
      // 422 (ex.: kit em uso sem ficha escolhida): a mensagem da API aparece e a navegacao segue para a versao nova.
      setErroAcao(mensagemErro(e));
      setPerguntaAtiva(null);
      if (p.depois) setIrPara(p.depois);
    } finally {
      setMarcandoAtiva(false);
    }
  }

  function desfazer() {
    const l = deFicha(ficha);
    const r = rendimentoDe(ficha);
    setLinhas(l);
    setDescricao(ficha.descricao ?? '');
    setRendimento(r);
    setBase(assinatura(l, ficha.descricao ?? '', r));
    setErro(null);
  }

  const abrir = (id: string | undefined) => {
    if (id) void navigate({ to: '/custos/fichas-tecnicas/$id', params: { id } });
  };

  const tiposMaterial = tiposDeMaterial(item.tipo);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Fichas técnicas <span aria-hidden="true">/</span> {item.codigo}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2.5">
            <h1 className="text-display font-semibold tracking-[-0.01em]">{item.descricao}</h1>
            <span className="rounded-full bg-line-soft px-2.5 py-[3px] text-caption font-semibold text-ink-body">{ROTULO_TIPO[item.tipo]}</span>
            {doProtheus && (
              <span data-testid="selo-ficha-protheus" className="inline-flex items-center gap-2 text-small text-ink-secondary">
                <SeloProtheus />
                Ficha vinda do Protheus (somente leitura)
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 text-ink-secondary">
            <span data-testid="versao-aberta">
              Ficha técnica · versão {ficha.versao}
              {ficha.escolhida ? ' (escolhida)' : ''}
            </span>
            <Link to="/cadastros/itens/$id" params={{ id: item.id }} className="text-small font-medium text-primary hover:underline">
              Abrir cadastro do item
            </Link>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HistoricoRegistro recurso="fichas_tecnicas" chave={ficha.id} />
          <BotaoRelatorios tela="ficha" params={{ fichaId: ficha.id }} />
        </div>
      </div>

      <div role="tablist" aria-label="Visões da ficha" className="flex gap-1 border-b border-border">
        {(
          [
            ['ficha', 'Ficha'],
            ['cascata', 'Ficha em cascata'],
            ['usada', 'Usada em'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            role="tab"
            aria-selected={guia === valor}
            onClick={() => setGuia(valor)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-small font-medium',
              guia === valor ? 'border-primary text-ink' : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {guia === 'ficha' && (
        <>
      {ficha.emUso && (
        <div role="status" data-testid="aviso-em-uso" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          <b>Esta versão já foi usada em uma formação de preço ou orçamento e não pode ser alterada.</b> Se você editar os componentes, ao salvar eles
          viram uma nova versão; esta continua intacta para o histórico.
        </div>
      )}
      {erroAcao && (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erroAcao}
        </div>
      )}
      {trocaFeita && (
        <section role="status" data-testid="aviso-troca-escolhida" className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
          <div>
            <div className="font-semibold">Ficha escolhida trocada</div>
            <p className="mt-1">
              Os produtos que usam este item como semiacabado podem ter ficado marcados como “custo atualizado — revisar preço”: o sugerido mudou e o
              praticado não muda sozinho. A marca aparece na lista de preços.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Link to="/precificar/lista" className="font-medium text-primary hover:underline">
              Revisar na lista de preços
            </Link>
            <button type="button" onClick={() => setTrocaFeita(false)} className="font-medium text-ink-secondary hover:underline">
              Dispensar aviso
            </button>
          </div>
        </section>
      )}
      {pendentes.length > 0 && (
        <section role="alert" data-testid="aviso-mao-de-obra-fora" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          <div className="font-semibold">Mão de obra fora do processo nesta ficha</div>
          <p className="mt-1">
            Num produto, o tempo de produção fica só nas etapas do processo (centro analítico + minutos). A ficha guarda as linhas de mão de obra
            antigas com o custo gravado; para gravar de novo, transforme cada uma em etapa (escolha o centro) ou remova-a.
          </p>
          <ul className="mt-2 list-disc pl-5">
            {pendentes.map((a, i) => (
              <li key={`${a.ordem ?? 'x'}-${i}`}>
                {a.descricao ? <span className="font-medium">{a.descricao}</span> : 'Linha de mão de obra'}
                {a.ordem ? ` (linha ${a.ordem})` : ''} · {a.message}
              </li>
            ))}
          </ul>
        </section>
      )}
      {errosLinhas.length > 0 && (
        <section role="alert" data-testid="erros-ficha" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          <div className="font-semibold">Corrija:</div>
          <ul className="mt-1 list-disc pl-5">
            {listaDeErros(errosLinhas).map((texto) => (
              <li key={texto}>{texto}</li>
            ))}
          </ul>
        </section>
      )}

      {comCusto && (
        <section aria-label="Resumo do custo" className="grid gap-3 rounded-2xl border border-border bg-card px-5 py-4 sm:grid-cols-4">
          <Resumo rotulo="Processo (etapas)" valor={processoTotal} testid="custo-processo-total" />
          <Resumo rotulo="Materiais" valor={materiaisTotal} testid="custo-materiais-total" detalhe={materiaisSemi ? `dos quais semiacabados ${formatarCusto(materiaisSemi)}` : undefined} />
          <Resumo rotulo="Custo da ficha (total)" valor={totalApi} testid="custo-unitario-ficha" destaque aguardando={calculando} />
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setAtencaoAberta(true)}
            className="block rounded-md text-left hover:bg-line-soft focus-visible:outline-none focus-visible:shadow-focus"
          >
            <span className="block text-small text-ink-muted">Linhas que precisam de atenção</span>
            <span className="block tabular text-metric font-semibold" data-testid="linhas-com-alerta">
              {comAlerta}
            </span>
            <span className="block text-caption text-ink-muted">{calculando ? 'Recalculando na API…' : 'Vindo da API · ver o motivo'}</span>
          </button>
        </section>
      )}

      {!servico && (
      <section aria-label="Processo produtivo" className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-title font-semibold">Processo produtivo</h2>
            <div className="text-small text-ink-muted">
              Etapas: centro analítico e minutos. Setas, Tab e Enter navegam; digite para editar. Minutos × CPM do centro na data = custo da etapa.
            </div>
          </div>
          {podeEditarComp && (
            <button type="button" onClick={adicionarEtapa} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
              + Etapa
            </button>
          )}
        </div>
        <GradeProcesso
          linhas={processo}
          calculo={calculo}
          editavel={podeEditarComp}
          comCusto={comCusto}
          centros={centros}
          erros={marcasDeErro}
          onChange={setProcesso}
          focar={focarEtapa}
        />
        {processo.length === 0 && (
          <p role="status" className="border-t border-border px-5 py-6 text-center text-small text-ink-muted">
            Esta ficha ainda não tem etapas de processo. Use “+ Etapa” e escolha o centro analítico.
          </p>
        )}
        {comCusto && (
          <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3 text-small">
            <span className="text-ink-secondary">Total do processo</span>
            <span className="tabular font-semibold" data-testid="processo-total">
              {processoTotal === null ? (dirty ? '…' : '—') : formatarCusto(processoTotal)}
            </span>
          </div>
        )}
      </section>
      )}

      <section aria-label="Rendimento do processo" className="rounded-2xl border border-border bg-card px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-[minmax(160px,220px)_minmax(220px,320px)] sm:items-end">
          <div>
            <Label htmlFor="r-processo" className={ROTULO_CAMPO}>
              Rendimento do processo
            </Label>
            <div className="flex h-10 items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
              <input
                id="r-processo"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="sem rendimento"
                value={rendimento.processo}
                onChange={(e) => setRendimento((r) => ({ ...r, processo: e.target.value }))}
                disabled={!rendimentoEditavel}
                aria-invalid={rend.erro ? true : undefined}
                aria-describedby={rend.erro ? 'e-rendimento' : undefined}
                className="tabular w-full border-0 bg-transparent font-semibold outline-none disabled:opacity-60"
              />
              <span aria-hidden="true" className="text-ink-muted">
                %
              </span>
            </div>
          </div>
          <div>
            <Label htmlFor="r-grupo" className={ROTULO_CAMPO}>
              Grupo de insumos
            </Label>
            <Select
              value={rendimento.grupo}
              onValueChange={(v) => setRendimento((r) => ({ ...r, grupo: v as RendimentoGrupo }))}
              disabled={!rendimentoEditavel}
            >
              <SelectTrigger id="r-grupo" className="h-10 w-full rounded-[10px] bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GRUPOS_RENDIMENTO.map((g) => (
                  <SelectItem key={g} value={g}>
                    {ROTULO_GRUPO_RENDIMENTO[g]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="mt-2 text-small text-ink-muted" data-testid="ajuda-rendimento">
          O rendimento r (ex.: 80%) divide as quantidades do grupo de insumos escolhido E os minutos das etapas: custo do processo = minutos ÷ r × CPM.
          {/* O exemplo tem R$: so para quem ve custo (PCP nao ve nenhum valor em R$). */}
          {comCusto && ' Ex.: 10 min a R$ 0,50 com rendimento 80% = 12,5 min = R$ 6,25 (sem rendimento: R$ 5,00).'}
        </p>
        {rend.erro && (
          <p id="e-rendimento" role="alert" className="mt-1 text-caption text-danger-ink">
            {rend.erro}
          </p>
        )}
        {ficha.emUso && <p className="mt-1 text-caption text-warning-ink">Ficha em uso: para mudar o rendimento, crie uma nova versão.</p>}
      </section>

      {servico && (
        <section aria-label="Composição de horas" className="rounded-2xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div>
              <h2 className="text-title font-semibold">Composição de horas</h2>
              <div className="text-small text-ink-muted" data-testid="ajuda-composicao">
                Serviço: some as horas de cada profissional ou equipe vezes o custo por hora.
              </div>
            </div>
            {podeEditarComp && (
              <button type="button" onClick={adicionarHoras} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
                Adicionar linha de horas
              </button>
            )}
          </div>
          <GradeMateriais
            composicaoHoras
            linhas={materiais}
            calculo={calculo}
            editavel={podeEditarComp}
            comCusto={comCusto}
            tipos={['MAO_DE_OBRA']}
            centros={centros}
            erros={marcasDeErro}
            onChange={setMateriais}
            focar={focarMat}
          />
          {materiais.length === 0 && (
            <p role="status" className="border-t border-border px-5 py-6 text-center text-small text-ink-muted">
              Esta ficha ainda não tem horas. Use “Adicionar linha de horas” e informe o profissional ou centro, as horas e o custo por hora.
            </p>
          )}
          {comCusto && (
            <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3 text-small">
              <span className="text-ink-secondary">Total da composição</span>
              <span className="tabular font-semibold" data-testid="composicao-total">
                {materiaisTotal === null ? (dirty ? '…' : '—') : formatarCusto(materiaisTotal)}
              </span>
            </div>
          )}
        </section>
      )}

      {!servico && (
      <section aria-label="Materiais" className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-title font-semibold">Materiais</h2>
            <div className="text-small text-ink-muted">
              Item, centro de consumo (opcional, informativo), quantidade, unidade e custo. Ctrl+V cola um bloco do Excel.
            </div>
          </div>
          {podeEditarComp && (
            <button type="button" onClick={() => adicionarMaterial()} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
              + Linha
            </button>
          )}
        </div>
        {ehCoproduto && (
          <div role="status" data-testid="aviso-ficha-coproduto" className="flex flex-wrap items-center gap-x-3 border-b border-border bg-warning-bg px-5 py-3 text-small text-warning-ink">
            <span>O custo desta ficha vem do rateio do custo conjunto da ficha do item principal.</span>
            {fichaPrincipal && (
              <Link to="/custos/fichas-tecnicas/$id" params={{ id: fichaPrincipal }} className="font-medium text-primary hover:underline">
                Abrir ficha do item principal
              </Link>
            )}
          </div>
        )}
        {podeEditarComp && (
          <div className="border-b border-border px-5 py-3">
            <div className="flex max-w-xl flex-col gap-1 text-caption font-medium text-ink-muted">
              Adicionar item do cadastro
              <ItemCombobox
                valor={null}
                onSelecionar={adicionarItem}
                itens={candidatos}
                carregando={itens.isFetching}
                erro={itens.error?.message ?? null}
                termo={termo}
                onTermoChange={setTermo}
                placeholder="Buscar insumo ou mercadoria (código ou descrição)…"
                ariaLabel="Adicionar item componente"
              />
            </div>
          </div>
        )}
        {podeEditarComp && (
          <div className="flex justify-end px-5 pt-2">
            <label className="flex items-center gap-2 text-caption text-ink-muted">
              <input type="checkbox" checked={perdaManual} onChange={(e) => setPerdaManual(e.target.checked)} />
              Perda por linha
            </label>
          </div>
        )}
        <GradeMateriais
          mostrarPerda={perdaManual}
          linhas={materiais}
          calculo={calculo}
          editavel={podeEditarComp}
          comCusto={comCusto}
          tipos={tiposMaterial}
          centros={centros}
          centrosDeConsumo={centrosDeConsumo}
          erros={marcasDeErro}
          itemIdDaFicha={item.id}
          fornecedores={fornecedores.data ?? []}
          onChange={setMateriais}
          focar={focarMat}
        />
        {materiais.length === 0 && (
          <p role="status" className="border-t border-border px-5 py-6 text-center text-small text-ink-muted">
            Esta versão ainda não tem materiais. Use “+ Linha”, busque um item do cadastro ou cole um bloco do Excel.
          </p>
        )}
        {comCusto && (
          <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3 text-small">
            <span className="text-ink-secondary">Total dos materiais</span>
            <span className="tabular font-semibold" data-testid="materiais-total">
              {materiaisTotal === null ? (dirty ? '…' : '—') : formatarCusto(materiaisTotal)}
            </span>
          </div>
        )}
        {comCusto && ficha.materiais?.totalServicosTerceiros !== undefined && (
          <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-3 text-small">
            <span className="text-ink-secondary">Serviços de terceiros</span>
            <span className="tabular font-semibold" data-testid="servicos-terceiros-total">
              {dirty ? '…' : formatarCusto(ficha.materiais.totalServicosTerceiros)}
            </span>
          </div>
        )}
      </section>
      )}

      <SaidasFicha
        fichaId={ficha.id}
        principal={{ codigo: item.codigo, descricao: item.descricao, unidade: item.unidadeCusto || item.unidade }}
        quantidadeSaida={ficha.quantidadeSaida}
        criterioRateio={ficha.criterioRateio}
        saidas={ficha.saidas ?? []}
        comErro={errosSaidas.length > 0}
        textoErro={errosSaidas.map((a) => a.message).join(' ')}
        editavel={podeEditar && comCusto && !ficha.emUso}
        comCusto={comCusto}
        motivoNaoEditavel={doProtheus ? 'Ficha vinda do Protheus: somente leitura.' : motivoSaidasNaoEditavel({ podeEditar, comCusto, emUso: ficha.emUso })}
      />
      <RateioFicha rateio={ficha.rateio} comCusto={comCusto} />

      <PainelCustoFicha ficha={ficha} comCusto={comCusto} />

      <VersoesFicha
        versoes={versoes}
        atualId={ficha.id}
        sujo={dirty}
        ocupado={escolher.isPending || novaVersao.isPending}
        onAbrir={abrir}
        onEscolher={(id) => void marcarEscolhida(id)}
        onNova={() => void criarNovaVersao()}
        podeEditar={podeEditar}
        comCusto={comCusto}
      />

      {item.id && <HistoricoEscolhas itemId={item.id} />}

      {!doProtheus && (
      <section aria-label="Ao salvar uma nova versão" className="rounded-xl border border-[#FED7AA] bg-warning-bg px-[18px] py-4 text-small text-[#7C2D12]">
        <div className="font-semibold">Ao salvar uma nova versão</div>
        <div className="mt-1">
          A nova versão só passa a valer na formação de preço e nos orçamentos quando for marcada como escolhida. Orçamentos e formações já salvos não
          mudam: guardam o custo da época.
        </div>
      </section>
      )}

      {podeEditar && (
        <RecordToolbar
          dirty={dirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erro}
          index={posicao >= 0 ? versoes.length - posicao : undefined}
          total={posicao >= 0 ? versoes.length : undefined}
          canPrev={Boolean(maisAntiga) && !dirty}
          canNext={Boolean(maisNova) && !dirty}
          onList={() => void navigate({ to: '/cadastros/itens/$id', params: { id: item.id }, search: {} })}
          onSave={() => void salvar()}
          onNew={() => void criarNovaVersao()}
          onUndo={desfazer}
          onPrev={() => abrir(maisAntiga?.id)}
          onNext={() => abrir(maisNova?.id)}
          onDelete={() => undefined}
          excluirTitulo="Fichas não são excluídas"
          excluirConfirmar="Entendi"
          excluirDescricao="Uma versão da ficha pode já ter sido usada em formações e orçamentos, e esse histórico precisa continuar legível. Para mudar o custo, crie uma nova versão e marque-a como escolhida."
        />
      )}
        </>
      )}
      {guia === 'cascata' && <CascataFicha fichaId={ficha.id} />}
      {guia === 'usada' && <UsadaEm itemId={ficha.itemId} />}
      <ListaAtencao itens={itensAtencao} aberta={atencaoAberta} onAbertaChange={setAtencaoAberta} />
      <SaidaComAlteracoesDialog {...bloqueio} />
      <AlertDialog open={perguntaAtiva !== null} onOpenChange={() => undefined}>
        <AlertDialogContent data-testid="pergunta-ficha-ativa">
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar como a ficha ativa de {item.descricao}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta versão{perguntaAtiva?.versao ? ` (versão ${perguntaAtiva.versao})` : ''} foi salva, mas não é a ficha ativa do produto. A ficha ativa é a que a Formação de preço, a Lista e os orçamentos usam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button type="button" variant="outline" disabled={marcandoAtiva} onClick={() => void responderAtiva(false)}>
              Não, manter a atual
            </Button>
            <Button type="button" disabled={marcandoAtiva} onClick={() => void responderAtiva(true)}>
              Sim, marcar como ativa
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {dialogo}
    </>
  );
}

/** Um valor do resumo: o que a API mandou (com R$) ou "…" enquanto o gravado nao vale (alterado sem salvar). */
function Resumo({
  rotulo,
  valor,
  testid,
  detalhe,
  destaque,
  aguardando,
}: {
  rotulo: string;
  valor: string | null;
  testid: string;
  detalhe?: string;
  destaque?: boolean;
  aguardando?: boolean;
}) {
  return (
    <div>
      <div className="text-small text-ink-muted">{rotulo}</div>
      <div className={destaque ? 'tabular text-display font-semibold' : 'tabular text-metric font-semibold'} data-testid={testid} aria-busy={aguardando || undefined}>
        {valor === null ? '…' : formatarCusto(valor)}
      </div>
      {detalhe && <div className="text-caption text-ink-muted">{detalhe}</div>}
    </div>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
